import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle, Calendar, MapPin, Car, ArrowRight, Loader2 } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export function BookingSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Stripe returns session_id as the booking_id in our success URL
  const sessionId = searchParams.get('session_id');
  const bookingId = searchParams.get('booking_id');
  const type = searchParams.get('type');
  
  // Use session_id if booking_id not present (Stripe template variable)
  const effectiveBookingId = bookingId || sessionId;
  
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pollAttempts, setPollAttempts] = useState(0);

  useEffect(() => {
    const fetchBooking = async () => {
      if (!effectiveBookingId) {
        setLoading(false);
        return;
      }
      
      try {
        const res = await axios.get(`${API}/api/stripe/booking/${effectiveBookingId}`);
        setBooking(res.data);
        
        // If payment is still pending, poll for status
        if (res.data.status === 'pending' && pollAttempts < 5) {
          setTimeout(() => {
            setPollAttempts(prev => prev + 1);
          }, 2000);
        } else {
          setLoading(false);
        }
      } catch (err) {
        console.error("Failed to fetch booking:", err);
        setLoading(false);
      }
    };
    
    fetchBooking();
  }, [effectiveBookingId, pollAttempts]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 size={32} className="text-[#3b82f6] animate-spin" />
      </div>
    );
  }

  const isInspection = type === 'inspection';
  const isHeadlight = type === 'headlight';

  return (
    <div className="min-h-screen bg-[#050505] pt-28 pb-20" data-testid="booking-success-page">
      <div className="max-w-xl mx-auto px-6 text-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="w-20 h-20 bg-[#22c55e]/20 border border-[#22c55e]/50 flex items-center justify-center mx-auto mb-8"
        >
          <CheckCircle size={40} className="text-[#22c55e]" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h1 className="font-heading text-4xl sm:text-5xl uppercase tracking-tight text-white mb-4">
            {isInspection ? "Inspection Booked!" : isHeadlight ? "Deposit Received!" : "Booking Confirmed!"}
          </h1>
          <p className="text-zinc-400 font-body text-base mb-8">
            {isInspection 
              ? "We'll see you at your location. Check your email for confirmation."
              : isHeadlight
              ? "Your headlight restoration is booked. Check your email for details."
              : "Your booking has been confirmed. Check your email for details."
            }
          </p>
        </motion.div>

        {booking && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-[#09090b] border border-white/5 p-6 text-left mb-8"
          >
            <h3 className="font-heading text-sm uppercase tracking-wider text-zinc-500 mb-4">Booking Details</h3>
            
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-white">
                <Car size={16} className="text-[#3b82f6]" />
                <span className="font-body">
                  {booking.vehicle_year} {booking.vehicle_make} {booking.vehicle_model}
                </span>
              </div>
              
              <div className="flex items-center gap-3 text-white">
                <Calendar size={16} className="text-[#3b82f6]" />
                <span className="font-body">
                  {booking.preferred_date} at {booking.preferred_time}
                </span>
              </div>
              
              {booking.address && (
                <div className="flex items-center gap-3 text-white">
                  <MapPin size={16} className="text-[#3b82f6]" />
                  <span className="font-body">
                    {booking.address}, {booking.suburb}
                  </span>
                </div>
              )}
            </div>

            <div className="mt-6 pt-4 border-t border-white/5">
              {isInspection ? (
                <div className="flex items-center justify-between">
                  <span className="text-zinc-500 font-body">Amount Paid</span>
                  <span className="text-[#22c55e] font-heading text-xl">$150.00</span>
                </div>
              ) : isHeadlight ? (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-zinc-500 font-body">Deposit Paid</span>
                    <span className="text-[#22c55e] font-heading text-xl">$50.00</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-500 font-body">Remaining (due on completion)</span>
                    <span className="text-white font-heading text-xl">$200.00</span>
                  </div>
                </>
              ) : null}
            </div>
          </motion.div>
        )}

        {isInspection && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-[#22c55e]/10 border border-[#22c55e]/30 p-4 mb-8"
          >
            <p className="text-[#22c55e] font-body text-sm">
              💰 If you proceed with a full respray, this $150 will be credited towards your total!
            </p>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <button
            onClick={() => navigate("/")}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-white text-black font-heading text-sm tracking-[0.15em] uppercase hover:bg-zinc-200 transition-colors cursor-pointer border-none"
          >
            Back to Home
            <ArrowRight size={14} />
          </button>
        </motion.div>

        <p className="text-zinc-600 text-xs mt-8">
          Questions? Call us at 0412 345 678 or email bookings@velocitypaintandpanel.com
        </p>
      </div>
    </div>
  );
}

export default BookingSuccessPage;
